import { inject, injectable } from 'inversify';
import { isAxiosError } from 'axios';
import { EventDependencyTypes, type EventManager } from '@kiplin/web-common/event';
import { NetworkDependencyTypes } from '@shared/common/network/network.dependency-types';
import type { JwtRepositoryInterface } from '@shared/common/network/domain/repository/jwt.repository';
import { CurrentUserDependencyTypes } from '@shared/entity/current-user/current-user.dependency-types';
import type { CurrentUserStoreResetter } from '@shared/entity/current-user/domain/service/current-user-store-resetter.service';
import { AuthenticationRequiredEvent } from '@shared/common/network/domain/event/authentication-required.event';

@injectable()
export class AuthenticationRequiredService {
  public constructor(
    @inject(EventDependencyTypes.EventManager)
    private readonly eventManager: EventManager,
    @inject(NetworkDependencyTypes.JwtRepository)
    private readonly jwtRepository: JwtRepositoryInterface,
    @inject(CurrentUserDependencyTypes.StoreResetter)
    private readonly currentUserStoreResetter: CurrentUserStoreResetter,
  ) {}

  public isAuthenticationRequiredError(error: unknown): boolean {
    if (!isAxiosError(error)) {
      return false;
    }

    return error.response?.status === 401 || error.response?.status === 403;
  }

  public dispatchAuthenticationRequired(error: unknown): boolean {
    if (!isAxiosError(error) || !this.isAuthenticationRequiredError(error)) {
      return false;
    }

    this.eventManager.dispatch(
      new AuthenticationRequiredEvent({
        detail: {
          statusCode: error.response?.status ?? 0,
          url: error.config?.url ?? null,
        },
      }),
    );

    return true;
  }

  public redirectToLoginOnAuthenticationRequired(error: unknown): boolean {
    if (!this.isAuthenticationRequiredError(error)) {
      return false;
    }

    this.clearAuthentication();
    return true;
  }

  public clearAuthentication(): void {
    this.jwtRepository.clearToken();
    this.currentUserStoreResetter.reset();
  }
}
